/**
 * How Luma's lines are cut into pieces for her voice (2026-10-06). The server makes each piece
 * whole before it can be heard: about 0.4 s plus the speech's length divided by its speed (2.4
 * times real time on a desktop CPU, about real time on the ARM server). The first piece is
 * short so she starts within about a second, and every later one is short too, so each is made
 * while the one before plays: pieces that grew (80, then 140 characters) outran the server's
 * CPU and left her silent mid-answer for 3 s. The client and the server (which starts voicing
 * an answer as soon as it is written) cut the same way, so they agree on what is cached.
 */

/** Longest first piece, in characters (about 3 s of speech). */
export const SPEECH_FIRST = 45;
/** Longest later piece (about 4 s): gapless while the voice runs above 1.2 times real time. */
export const SPEECH_PIECE = 60;
const SENTENCE_MAX = 220;

/** Splits text into sentence-sized pieces (short ones merged). */
export function sentences(text: string, max = SENTENCE_MAX): string[] {
  const clean = text
    .replace(/[*_#`>]/g, '')
    .replace(/\s*\n+\s*/g, '. ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return [];
  const parts = clean.match(/[^.!?;:]+(?:[.!?;:]+(?=\s|$)|$)/g)?.map((s) => s.trim()) ?? [clean];
  const out: string[] = [];
  for (const p of parts.filter(Boolean)) {
    const last = out[out.length - 1];
    if (last && (last.length < 40 || p.length < 12) && last.length + p.length + 1 <= max)
      out[out.length - 1] = `${last} ${p}`;
    else if (p.length <= max) out.push(p);
    else {
      // A very long sentence: break at commas, then at spaces.
      let rest = p;
      while (rest.length > max) {
        const cut = Math.max(rest.lastIndexOf(', ', max), rest.lastIndexOf(' ', max));
        const at = cut > 40 ? cut + 1 : max;
        out.push(rest.slice(0, at).trim());
        rest = rest.slice(at).trim();
      }
      if (rest) out.push(rest);
    }
  }
  return out;
}

/**
 * Cuts a piece longer than `max` (with a little slack) at the last pause (comma, semicolon,
 * colon, dash) inside it, else at the last space; a cut nearer the start than 18 characters
 * would leave a stub, so then it stays whole.
 */
function cutHead(piece: string, max: number): [string, string] | undefined {
  if (piece.length <= max + 12) return undefined;
  const limit = max + 12;
  let at = -1;
  for (const m of piece.matchAll(/[,;:—–]\s/g)) if (m.index! + 1 <= limit) at = m.index! + 1;
  if (at < 18) at = piece.lastIndexOf(' ', max);
  if (at < 18) return undefined;
  return [piece.slice(0, at).trim(), piece.slice(at).trim()];
}

/** A line as pieces to voice: every one short, the first shortest. */
export function speechChunks(text: string): string[] {
  const pieces = sentences(text);
  for (let i = 0; i < pieces.length; i++) {
    const cut = cutHead(pieces[i]!, i === 0 ? SPEECH_FIRST : SPEECH_PIECE);
    if (cut) pieces.splice(i, 1, cut[0], cut[1]);
  }
  return pieces;
}
